import React, { useState, useEffect } from 'react';
import { SubscriptionPlan, TenantSubscription, DashboardOverviewResponse } from '../types';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Toast from '../components/ui/Toast';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { handleApiError } from '../utils/errorHandling';
import { getPlanTier } from '../utils/planTierConfig';
import { useLocale } from '../context/LocaleContext';

interface GetPlansResponse {
    plans: SubscriptionPlan[];
}

const SubscriptionsPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSub, setCurrentSub] = useState<TenantSubscription | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingPlanId, setSubmittingPlanId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{type: 'success' | 'info', message: string} | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { setSubscriptionError } = useSubscriptionError();

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleCancel = async () => {
    setShowCancelConfirm(true);
  }

  const confirmCancel = async () => {
    setError(null);
    setSubmittingPlanId('cancel');
    try {
        await api.delete<TenantSubscription>('/api/subscriptions/tenant');
        setCurrentSub(null);
        setNotification({ type: 'info', message: 'Your subscription has been cancelled successfully.' });
        setSubscriptionError(null);
    } catch (err: any) {
        console.error('Cancellation error:', err);
        handleApiError(err, setError, setSubscriptionError);
    } finally {
        setSubmittingPlanId(null);
        setShowCancelConfirm(false);
    }
  }

  const fetchSubscriptionData = async () => {
    try {
      const subResponse = await api.get<TenantSubscription>('/api/subscriptions/tenant').catch(e => {
        if (e.code === 'TENANT0004') {
          return null;
        }
        if (e.message && e.message.includes('404')) {
          return null;
        }
        throw e;
      });

      if (subResponse && (!subResponse.planId || !subResponse.status)) {
        console.warn('Invalid subscription data structure:', subResponse);
        return null;
      }

      setCurrentSub(subResponse);
      return subResponse;
    } catch (err: any) {
      handleApiError(err, setError, setSubscriptionError);
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch dashboard overview for LLM usage
        const dashboardPromise = api.getDashboardOverview()
            .then(data => setDashboardData(data))
            .catch(() => setDashboardData(null));

        const plansPromise = api.get<GetPlansResponse>('/api/subscriptions/plans');
        
        const [plansResponse] = await Promise.all([
            plansPromise,
            fetchSubscriptionData(),
            dashboardPromise
        ]);

        const validPlans = plansResponse.plans?.filter(plan =>
          plan &&
          plan.planId &&
          plan.name &&
          plan.pricing &&
          typeof plan.pricing.amount === 'number'
        ) || [];

        setPlans(validPlans);
        setSubscriptionError(null);
      } catch (err: any) {
        handleApiError(err, setError, setSubscriptionError);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
        <div className="flex justify-center items-center h-full">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
    );
  }

  if (error) {
      return <div className="text-center text-red-500">{error}</div>
  }

  const currentPlan = plans.find(p => p.planId === currentSub?.planId);
  const tier = currentSub?.planId ? getPlanTier(currentSub.planId) : 'Unknown';
  const tierDisplay = tier.charAt(0).toUpperCase() + tier.slice(1);
  
  const { formatCurrency, formatDate } = useLocale();
  
  const baseCost = currentSub?.billing?.amount || 0;
  const llmUsageCost = dashboardData?.data?.usageOverview?.monthlyCostToDate || 0;
  const customSolutionCost = 0;

  const totalCost = baseCost + llmUsageCost + customSolutionCost;

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 dark:text-text-primary text-light-text-primary ">My Subscription</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-secondary dark:text-text-secondary text-light-text-secondary">Current Subscription Status</h2>
                
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            currentSub?.status === 'active' ? 'bg-green-500/20 text-green-500' :
                            currentSub?.status === 'trial' ? 'bg-blue-500/20 text-blue-500' :
                            'bg-gray-500/20 text-gray-500'
                        }`}>
                            {(currentSub?.status || 'No Active Subscription').toUpperCase()}
                        </span>
                    </div>

                    {currentSub?.planId && (
                         <div className="flex justify-between items-center">
                            <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Plan Type</span>
                            <span className="font-medium text-white dark:text-white text-gray-900">{tierDisplay}</span>
                         </div>
                    )}
                     
                     <div className="flex justify-between items-center">
                        <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Next Billing</span>
                        <span className="text-white dark:text-white text-gray-900">
                             {currentSub?.billing?.nextBillingDate 
                                ? formatDate(currentSub.billing.nextBillingDate) 
                                : 'N/A'}
                        </span>
                     </div>

                     {currentSub?.status === 'active' && currentSub.planId && (
                        <div className="pt-4 mt-4 border-t border-border/50">
                             <Button 
                                variant="danger" 
                                className="w-full" 
                                onClick={handleCancel} 
                                isLoading={submittingPlanId === 'cancel'} 
                                disabled={submittingPlanId !== null}
                             >
                                Cancel Subscription
                             </Button>
                        </div>
                     )}
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-secondary dark:text-text-secondary text-light-text-secondary">Cost Breakdown</h2>
                
                <div className="space-y-4">
                    <div className="flex justify-between items-center pb-4 border-b border-border/50">
                        <div>
                             <div className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Base Cost</div>
                             <div className="text-xs text-text-tertiary">{(tierDisplay === 'Unknown') ? 'Standard' : tierDisplay} Plan</div>
                        </div>
                        <span className="font-mono text-lg dark:text-white text-gray-900">{formatCurrency(baseCost, 'EUR')}</span>
                    </div>

                    <div className="flex justify-between items-center pb-4 border-b border-border/50">
                        <div>
                            <div className="text-text-secondary dark:text-text-secondary text-light-text-secondary">LLM Usage</div>
                            <div className="text-xs text-text-tertiary">Live Cumulative</div>
                        </div>
                        <span className="font-mono text-lg dark:text-white text-gray-900">{formatCurrency(llmUsageCost, 'EUR')}</span>
                    </div>

                    <div className="flex justify-between items-center pb-4 border-b border-border/50">
                        <div>
                            <div className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Custom Solution</div>
                            <div className="text-xs text-text-tertiary">Enterprise Integrations</div>
                        </div>
                        <span className="font-mono text-lg dark:text-white text-gray-900">{formatCurrency(customSolutionCost, 'EUR')}</span>
                    </div>

                    <div className="flex justify-between items-center pt-2">
                        <span className="font-semibold text-lg text-white dark:text-white text-gray-900">Total</span>
                        <span className="font-mono text-2xl font-bold text-primary">{formatCurrency(totalCost, 'EUR')}</span>
                    </div>
                </div>
            </Card>
        </div>

        <div className="text-center text-sm text-text-tertiary mt-8">
            * Costs are estimated and updated in real-time based on usage.
        </div>

        {/* Mobile toast notification */}
        {notification && (
            <div className="fixed top-10 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)]">
            <Toast
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification(null)}
                duration={5000}
            />
            </div>
        )}

        {showCancelConfirm && currentSub && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
            <Card title="Cancel Subscription" className="w-full max-w-md m-4">
                <p className="dark:text-text-secondary text-light-text-secondary">
                Are you sure you want to cancel your subscription?
                </p>
                <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                Your access will continue until the end of your current billing period on {currentSub.billing?.nextBillingDate ? formatDate(currentSub.billing.nextBillingDate) : 'N/A'}.
                </p>
                <div className="flex justify-end gap-4 mt-6">
                <Button variant="secondary" onClick={() => setShowCancelConfirm(false)} disabled={submittingPlanId === 'cancel'}>
                    Go Back
                </Button>
                <Button
                    variant="danger"
                    onClick={confirmCancel}
                    isLoading={submittingPlanId === 'cancel'}
                >
                    Confirm Cancellation
                </Button>
                </div>
            </Card>
            </div>
        )}
    </div>
  );
};

export default SubscriptionsPage;
